import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ListFilter, Search } from 'lucide-react';
import { BoardSearchPickerShell } from './boardSearchPickerShell';
import { buildStateCityTree, filterStateCityTree } from './buildStateCityTree';

function buildCitiesByEstado(citiesWithCounts) {
  const map = {};
  citiesWithCounts.forEach((item) => {
    const estado = String(item.estado || '').trim() || '(Sem UF)';
    if (!map[estado]) map[estado] = [];
    map[estado].push(item.cidade);
  });
  return map;
}

export function BoardSearchCidadeFilterPanel({
  citiesWithCounts = [],
  citiesByEstado: citiesByEstadoProp,
  searchTerm,
  onSearchChange,
  selectedCidades = [],
  onToggleCity,
  onToggleEstado,
  onSetCidades,
}) {
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const bulkAnchorRef = useRef(null);

  const citiesByEstado = useMemo(
    () => citiesByEstadoProp || buildCitiesByEstado(citiesWithCounts),
    [citiesByEstadoProp, citiesWithCounts]
  );

  const stateTree = useMemo(() => buildStateCityTree(citiesWithCounts), [citiesWithCounts]);
  const filteredTree = useMemo(
    () => filterStateCityTree(stateTree, searchTerm),
    [stateTree, searchTerm]
  );

  const selectedSet = useMemo(() => new Set(selectedCidades), [selectedCidades]);

  const visibleCities = useMemo(
    () => filteredTree.flatMap(({ cities }) => cities.map((c) => c.cidade)),
    [filteredTree]
  );

  const allVisibleSelected =
    visibleCities.length > 0 && visibleCities.every((cidade) => selectedSet.has(cidade));

  useEffect(() => {
    if (!bulkMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (bulkAnchorRef.current?.contains(event.target)) return;
      setBulkMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [bulkMenuOpen]);

  const handleBulkToggle = () => {
    if (!onSetCidades || visibleCities.length === 0) return;
    const next = allVisibleSelected
      ? selectedCidades.filter((c) => !visibleCities.includes(c))
      : Array.from(new Set([...selectedCidades, ...visibleCities]));
    onSetCidades(next);
    setBulkMenuOpen(false);
  };

  return (
    <BoardSearchPickerShell>
      <div className="bsf-simple-picker-search">
        <Search size={13} strokeWidth={2} className="bsf-simple-picker-search-icon" aria-hidden />
        <input
          type="text"
          className="bsf-simple-picker-input"
          placeholder="Buscar UF ou cidade..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          autoFocus
        />
        {visibleCities.length > 1 ? (
          <div className="bsf-simple-picker-bulk-anchor" ref={bulkAnchorRef}>
            <button
              type="button"
              className="bsf-simple-picker-bulk-trigger"
              aria-label="Mais opções"
              aria-expanded={bulkMenuOpen}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                setBulkMenuOpen((open) => !open);
              }}
            >
              <ListFilter size={12} strokeWidth={2} aria-hidden />
            </button>
            {bulkMenuOpen ? (
              <div className="bsf-simple-picker-bulk-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="bsf-simple-picker-bulk-menu-item"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleBulkToggle}
                >
                  {allVisibleSelected ? 'Desmarcar tudo' : 'Selecionar tudo'}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="bsf-simple-picker-list" role="listbox" aria-label="Cidades por UF">
        {filteredTree.length > 0 ? (
          filteredTree.map(({ estado, cities }) => {
            const stateCities = citiesByEstado[estado] || [];
            const allStateSelected =
              stateCities.length > 0 && stateCities.every((c) => selectedSet.has(c));
            const stateCount = cities.reduce((acc, c) => acc + c.count, 0);

            return (
              <div key={estado} className="bsf-simple-picker-group">
                <button
                  type="button"
                  className={`bsf-simple-picker-item bsf-simple-picker-item--uf-head${
                    allStateSelected ? ' is-selected' : ''
                  }`}
                  aria-label={`Selecionar todas as cidades de ${estado}`}
                  aria-pressed={allStateSelected}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onToggleEstado(estado);
                  }}
                >
                  <span className="bsf-simple-picker-item-label">{estado}</span>
                  <span className="bsf-simple-picker-item-meta">{stateCount}</span>
                  <Check
                    size={12}
                    strokeWidth={2.5}
                    className="bsf-simple-picker-check"
                    aria-hidden
                  />
                </button>
                {cities.map(({ cidade, count }) => {
                  const selected = selectedSet.has(cidade);
                  return (
                    <button
                      key={`${estado}-${cidade}`}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`bsf-simple-picker-item bsf-simple-picker-item--city${
                        selected ? ' is-selected' : ''
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleCity(cidade);
                      }}
                    >
                      <span className="bsf-simple-picker-item-label" title={cidade}>
                        {cidade}
                      </span>
                      <span className="bsf-simple-picker-item-meta">{count}</span>
                      <Check
                        size={12}
                        strokeWidth={2.5}
                        className="bsf-simple-picker-check"
                        aria-hidden
                      />
                    </button>
                  );
                })}
              </div>
            );
          })
        ) : (
          <div className="bsf-simple-picker-empty">
            {searchTerm ? `Nenhum resultado para "${searchTerm}"` : 'Nenhuma cidade disponível'}
          </div>
        )}
      </div>
    </BoardSearchPickerShell>
  );
}
